import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, EnvelopeSimple, MapPin, Phone } from "@phosphor-icons/react";
import { FadeIn } from "../components/Motion";
import { SITE } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactBg =
  "https://images.unsplash.com/photo-1659776026027-6b0f66d92675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxsaXF1aWQlMjBjaHJvbWV8ZW58MHx8fHwxNzc2ODM4NjQwfDA&ixlib=rb-4.1.0&q=85";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={contactBg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAF6EF]/70 via-[#FAF6EF]/90 to-[#FAF6EF]" />
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <FadeIn>
            <div className="flex items-center justify-center gap-6 mb-8">
              <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
              <span className="issue-stripe">§ 05 · Contact</span>
              <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[108px] tracking-[-0.03em] font-semibold leading-[0.9] text-[#0b2e5e]">
              Tell us what
              <br />
              <span className="font-serif-italic text-[#e11d48]">you're</span> building.
            </h1>
            <p className="mt-10 max-w-2xl mx-auto text-lg md:text-xl text-[#0b2e5e]/75 leading-relaxed">
              Share the problem, the context, and the outcome you're chasing.
              We reply to every serious enquiry within one business day.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <div className="sticker"><span className="dot" style={{ background: "#14B8A6" }} /> 48-hour reply</div>
              <div className="sticker"><span className="dot" style={{ background: "#F97316" }} /> No pressure call</div>
              <div className="sticker"><span className="dot" style={{ background: "#8B5CF6" }} /> Booking Q1 2026</div>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <div className="glass rounded-3xl p-8 h-full space-y-8">
              <div>
                <div className="font-mono-mini text-[#00F0FF] mb-3">Reach</div>
                <div className="space-y-4 text-[#0b2e5e]/85">
                  <div className="flex items-start gap-3">
                    <EnvelopeSimple weight="duotone" size={20} className="text-[#FF3366] mt-1" />
                    <div>
                      <div className="text-xs text-[#0b2e5e]/50 font-mono-mini mb-1">Email</div>
                      <a href={`mailto:${SITE.email}`} className="hover:text-[#0b2e5e]">{SITE.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone weight="duotone" size={20} className="text-[#00F0FF] mt-1" />
                    <div>
                      <div className="text-xs text-[#0b2e5e]/50 font-mono-mini mb-1">Phone</div>
                      <span>{SITE.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin weight="duotone" size={20} className="text-[#0b2e5e] mt-1" />
                    <div>
                      <div className="text-xs text-[#0b2e5e]/50 font-mono-mini mb-1">Studio</div>
                      <span>{SITE.address}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="font-mono-mini text-[#FF3366] mb-3">Engagement</div>
                <ul className="space-y-2 text-[#0b2e5e]/75 text-sm">
                  <li>· Typical project: 4 to 12 weeks</li>
                  <li>· Dedicated team per project</li>
                  <li>· 48-hour response on all enquiries</li>
                </ul>
              </div>

              <div className="border-t border-[#0b2e5e]/12 pt-6">
                <div className="font-mono-mini text-[#0b2e5e]/50 mb-2">Currently booking</div>
                <div className="font-display text-2xl">Q2 & Q3 2026</div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="glass tracing-border rounded-3xl p-8 space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Full name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    className="ae-input"
                    placeholder="Jane Doe"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="ae-input"
                    placeholder="hello@company.com"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Company</label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    className="ae-input"
                    placeholder="Company name"
                    data-testid="contact-input-company"
                  />
                </div>
                <div>
                  <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Subject</label>
                  <input
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="ae-input"
                    placeholder="What's this about?"
                    data-testid="contact-input-subject"
                  />
                </div>
              </div>
              <div>
                <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Project details</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  className="ae-input resize-none"
                  placeholder="Tell us about your goals, timeline, and what good looks like…"
                  data-testid="contact-input-message"
                />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <div className="text-xs text-[#0b2e5e]/50 max-w-md">
                  By submitting, you agree to be contacted about your enquiry. We never share your info.
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {submitting ? "Sending…" : "Send enquiry"}
                  <ArrowUpRight weight="bold" size={16} />
                </button>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
